import { clamp } from 'lodash-es';
import { useCallback, useMemo, useRef } from 'react';
import { useStaticTabsStorage, type TabsState } from './TabsStorage';
import type { TabProps } from './types';
import type { UseTabsProps } from './useTabs';

export type PersistedDefaultTabProps<T extends TabProps = TabProps> = Omit<T, 'id'> & {
  id: string;
};
export interface PersistedTabProps<T extends TabProps> extends Pick<
  Partial<UseTabsProps<T>>,
  'onSelect' | 'onItemsChanged'
> {
  defaultInitialSelectedIndex?: number;
  /** initialize the tables with defaultInitialItems if there is no persisted data */
  defaultInitialItems?: PersistedDefaultTabProps<T>[];
  /** A list of IDs for tabs that should always exist and remain at the front of all tabs
   * Must be a subset of defaultInitialItems
   */
  excludedIDs?: string[];
}

/**
 * Apply display properties (label and tooltip) from default items to non-editable tabs.
 * If a tab is not editable, always use the original label and tooltip from defaultItems.
 */
function applyDisplayPropsToStaticTabs<T extends TabProps>(tabs: T[], defaultItems: T[]): T[] {
  return tabs.map(tab => {
    const defaultTab = defaultItems.find(t => t.id === tab.id);
    if (tab.editable !== true) {
      return {
        ...tab,
        label: defaultTab?.label ?? tab.label,
        tooltip: defaultTab?.tooltip ?? tab.tooltip,
      };
    }
    return tab;
  });
}

/**
 * Merge persisted tabs with default items according to excluded IDs.
 * Default items drive the actual names when IDs match, only using persisted names/tooltips when there's no default setting.
 */
export function mergePersistedAndDefaultItems<T extends TabProps>(
  persistedItems: T[],
  defaultItems: T[],
  excludedIDs: string[]
): T[] {
  const persistedItemsMap = new Map(persistedItems.map(item => [item.id, item]));
  const defaultExcludedItems = defaultItems.filter(tab => tab.id && excludedIDs.includes(tab.id));
  const mergedExcluded = defaultExcludedItems.map(tab => persistedItemsMap.get(tab.id) || tab);
  const nonExcludedPersistedItems = persistedItems
    .filter(tab => !excludedIDs.includes(tab.id as string))
    .map(item => ({ ...item, reorderable: true }));

  const mergedTabs = [...mergedExcluded, ...nonExcludedPersistedItems];
  return applyDisplayPropsToStaticTabs(mergedTabs, defaultItems);
}

export function usePersistedTabs<T extends TabProps = TabProps>(
  /** if tabsID is null, the tabs will not be persisted */
  tabsID: string | undefined,
  {
    defaultInitialSelectedIndex = -1,
    defaultInitialItems = [],
    onSelect,
    onItemsChanged,
    excludedIDs,
  }: PersistedTabProps<T>
): Required<Pick<UseTabsProps<T>, 'initialSelectedIndex' | 'onSelect' | 'onItemsChanged'>> & {
  initialItems: T[];
} {
  const { getState, setItems, setSelectedIndex } = useStaticTabsStorage();

  const handleSelect = useCallback(
    (index: number, tabs: T[]) => {
      if (tabsID == null) {
        return;
      }
      setSelectedIndex(tabsID, index);
      onSelect?.(index, tabs);
    },
    [onSelect, setSelectedIndex, tabsID]
  ) satisfies UseTabsProps<T>['onSelect'];

  const handleItemsChanged = useCallback(
    (items: T[]) => {
      if (tabsID == null) {
        return;
      }
      const tabs = items.filter(tab => !tab.isAddingTab && !tab.isTemporary);
      setItems(tabsID, tabs);
      onItemsChanged?.(items);
    },
    [onItemsChanged, setItems, tabsID]
  );

  const defaultInitialSelectedIndexRef = useRef(defaultInitialSelectedIndex);
  const defaultInitialItemsRef = useRef(defaultInitialItems);

  return useMemo(() => {
    const { items: initialItems, selectedIndex: initialSelectedIndex } = getPersistedTabs<T>(
      getState,
      tabsID,
      defaultInitialItemsRef.current as T[],
      defaultInitialSelectedIndexRef.current,
      excludedIDs
    );

    return {
      initialItems,
      initialSelectedIndex,
      onSelect: handleSelect,
      onItemsChanged: handleItemsChanged,
    };
  }, [getState, tabsID, excludedIDs, handleSelect, handleItemsChanged]);
}

function getPersistedTabs<T extends TabProps = TabProps>(
  getState: (tabsID: string) => TabsState<TabProps> | null,
  tabsID: string | undefined,
  defaultInitialItems: T[],
  defaultInitialSelectedIndex: number,
  excludedIDs: string[] | undefined
) {
  if (tabsID == null) {
    return {
      items: defaultInitialItems,
      selectedIndex: defaultInitialSelectedIndex,
    };
  }

  let items: T[];
  const persisted = getState(tabsID);

  if (persisted?.items) {
    items = mergePersistedAndDefaultItems(persisted.items as T[], defaultInitialItems, excludedIDs ?? []);
  } else {
    items = defaultInitialItems;
  }

  const selectedIndex = clamp(persisted?.selectedIndex ?? defaultInitialSelectedIndex, 0, items.length - 1);
  return { items, selectedIndex };
}
